// INDEX_SPLIT_B3_v1 (07.09.2026) -- Dashboard: SmsStatsWidget, AiChatStatsWidget, AiVisibilityWidget, DashboardPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { Activity, AlertCircle, Bar, BarChart, Bot, Calendar, CartesianGrid, Cell, CheckCircle, ChevronDown, Euro, MessageSquare, Pie, PieChart, ResponsiveContainer, Star, Tooltip, TrendingUp, UserPlus, Users, XAxis, YAxis, useEffect, useMemo, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, appointmentsWithoutReceipt, formatDate, isKosmetikSalon, shiftAppliesOn, today } from "../core/config.jsx";
import { Badge, Card, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";


// ============================================================
// PAGE: Dashboard
// ============================================================
// SMS_STATS_v1 -- Widget fuer SMS-Volumen im Owner-Dashboard
export const SmsStatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.request("/salon/sms-stats").then(r => {
      if (r && r.ok) setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  if (loading) return null;
  if (!stats) return null;
  // SMS_QUOTA_v1 -- wenn echtes Kontingent aktiv: Balken zeigt Inklusiv-Verbrauch statt Soft-Cap
  const q = stats.quota || {};
  const qOn = !!q.enabled;
  const pct = qOn
    ? (q.included > 0 ? Math.min(100, Math.round(q.used / q.included * 1000) / 10) : 100)
    : (stats.soft_cap_pct || 0);
  const remaining = qOn ? q.remaining : stats.soft_cap_remaining;
  const barColor = pct >= 90 ? COLORS.danger : pct >= 70 ? "#F59E0B" : COLORS.success;
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          <MessageSquare size={16} color={COLORS.primary} /> SMS-Versand diesen Monat
        </h3>
        <span style={{ fontSize: 11, color: COLORS.textLight }}>Plan: {stats.plan}</span>
      </div>
      {/* SMS_COST_HIDE_v1 -- Twilio-Kosten-Kachel entfernt (interne Plattformkosten, Salons sehen nur Stückzahlen) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>Heute</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{stats.today.count}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>Diesen Monat</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{stats.month.count}</div>
        </div>
      </div>
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
          <span>{qOn ? `Inklusiv-Kontingent (${q.included} SMS/Monat)` : `Empfohlenes Monatsvolumen (${stats.soft_cap} SMS)`}</span>
          <span>{qOn ? q.used : stats.month.count} / {qOn ? q.included : stats.soft_cap} ({pct}%)</span>
        </div>
        <div style={{ height: 8, background: "var(--cardHover)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: Math.min(100, pct) + "%", background: barColor, transition: "width 0.3s" }} />
        </div>
        <div style={{ fontSize: 10, color: (qOn && q.overage > 0 && q.billing_active) ? COLORS.danger : COLORS.textLight, marginTop: 4 }}>
          {/* SMS_QUOTA_BAR_NOW_v1 -- Kostenhinweis nur, wenn wirklich abgerechnet wird und das Kontingent ueberschritten ist */}
          {qOn
            ? (q.overage > 0 && q.billing_active
                ? `${q.overage} SMS über dem Kontingent — Mehrkosten ca. ${(q.overage_cost_eur != null ? q.overage_cost_eur : q.overage * q.overage_price_eur).toFixed(2)} € netto + USt., automatische Abrechnung am Monatsanfang.`
                : (q.overage > 0 ? `Kontingent diesen Monat ausgeschöpft.` : `Noch ${remaining} Inklusiv-SMS diesen Monat.`))
            : `Noch ${remaining} SMS unter dem empfohlenen Monatsvolumen.`}
        </div>
      </div>
    </Card>
  );
};

// AI_CHAT_TRACK_v1 -- Widget fuer KI-Chat-Volumen im Owner-Dashboard
export const AiChatStatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.request("/salon/ai-chat-stats").then(r => {
      if (r && r.ok) setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  if (loading || !stats) return null;
  const pct = stats.soft_cap_pct || 0;
  const remaining = stats.soft_cap_remaining;
  const barColor = pct >= 90 ? COLORS.danger : pct >= 70 ? "#F59E0B" : COLORS.success;
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          🤖 KI-Buchungs-Chat — diesen Monat
        </h3>
        <span style={{ fontSize: 11, color: COLORS.textLight }}>Plan: {stats.plan} · Modell: Haiku</span>
      </div>
      {/* SMS_COST_HIDE_v1 -- API-Kosten-Kachel entfernt (interne Plattformkosten, Salons sehen nur Stückzahlen) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>Heute</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{stats.today.count}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>Diesen Monat</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{stats.month.count}</div>
        </div>
      </div>
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
          <span>Inkludiert in deinem Plan ({stats.soft_cap} KI-Antworten)</span>
          <span>{stats.month.count} / {stats.soft_cap} ({pct}%)</span>
        </div>
        <div style={{ height: 8, background: "var(--cardHover)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: pct + "%", background: barColor, transition: "width 0.3s" }} />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 4 }}>
          {remaining > 0
            ? <>Noch {remaining} KI-Antworten inklusive — danach Fair-Use-Aufpreis (kommt im Q4 2026 als Hard-Cap).</>
            : <>Inkludierte Antworten verbraucht — KI funktioniert weiter, kleiner Fair-Use-Aufpreis ab Q4 2026.</>}
        </div>
      </div>
    </Card>
  );
};

// AI_VISIBILITY_v1 -- Owner-Widget: Salon-Sichtbarkeit in ChatGPT, Perplexity, Gemini, Google AI
export const AiVisibilityWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rechecking, setRechecking] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlError, setUrlError] = useState("");
  // AI_VISIBILITY_COMPLETED_HIDE_v1 -- wenn alles fertig (5/5 + Snippet drin),
  // ist die Anleitung/CTAs/Embed/FAQ obsolet. User kann via Toggle anzeigen.
  const [showDetailsWhenLive, setShowDetailsWhenLive] = useState(false);

  const saveWebsite = async () => {
    const v = urlInput.trim();
    if (!v) { setUrlError("Bitte URL eingeben"); return; }
    // einfache Validierung
    if (!/^https?:\/\/.+\..+/i.test(v) && !/^[a-z0-9.-]+\.[a-z]{2,}/i.test(v)) {
      setUrlError("Ungültige URL. Beispiel: https://www.dein-salon.de");
      return;
    }
    setSavingUrl(true);
    setUrlError("");
    try {
      const r = await api.request("/owner/ai-visibility/website", {
        method: "PUT",
        body: JSON.stringify({ website_url: v }),
      });
      if (r && r.ok) {
        setEditingUrl(false);
        setUrlInput("");
        load(); // neu laden — score wird automatisch geprüft
      } else {
        setUrlError((r && r.data && r.data.error) || "Speichern fehlgeschlagen");
      }
    } catch (e) {
      setUrlError("Netzwerk-Fehler — bitte erneut versuchen");
    }
    setSavingUrl(false);
  };

  const load = () => {
    setLoading(true);
    api.request("/owner/ai-visibility").then(r => {
      if (r && r.ok) setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const handleRecheck = async () => {
    setRechecking(true);
    try {
      const r = await api.request("/owner/ai-visibility/recheck", { method: "POST" });
      if (r && r.ok) setData(d => ({ ...d, ...r.data }));
    } catch (e) {}
    setRechecking(false);
  };

  // EMBED_COPY_FIX_v1 -- robuste Kopier-Funktion mit Fallback fuer aeltere Browser /
  // Non-Secure-Contexts (z.B. Vorschau ueber http://). Vorher: silent fail wenn clipboard
  // API blockiert war.
  const copyEmbed = () => {
    if (!data || !data.embed_snippet) return;
    const text = data.embed_snippet;
    const onOk = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    const onFail = () => {
      // Fallback: execCommand auf temporaerem textarea
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0"; ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) { onOk(); return; }
      } catch (e) { /* fall through */ }
      // Letzter Notnagel: Prompt zum manuellen Kopieren
      try { window.prompt("Code zum Kopieren (Cmd+C / Strg+C):", text); } catch (e) {}
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onOk).catch(onFail);
    } else {
      onFail();
    }
  };

  const mailToWebdev = () => {
    if (!data) return;
    const salonName = data.salon_name || "unseren Salon";
    const subject = encodeURIComponent("Bitte einbauen: KI-Sichtbarkeit-Snippet für " + salonName);
    const body = encodeURIComponent(
      "Hallo,\n\n" +
      "bitte folgenden Code-Snippet in den <head>-Bereich unserer Salon-Website einbauen.\n" +
      "Damit wird unser Salon in ChatGPT, Perplexity, Google AI Overviews und Gemini findbar.\n\n" +
      "════════════════════════════════════════════\n" +
      "1) CODE-SNIPPET (genau so in den <head> einfügen)\n" +
      "════════════════════════════════════════════\n\n" +
      (data.embed_snippet || "") + "\n\n" +
      "════════════════════════════════════════════\n" +
      "2) DATENSCHUTZ-ABSATZ (bitte in unsere Datenschutzerklärung einfügen)\n" +
      "════════════════════════════════════════════\n\n" +
      "Da der Snippet von shearly.pro nachgeladen wird, ist das rechtlich eine\n" +
      "Datenverarbeitung (IP-Adresse). Bitte folgenden Absatz in unsere\n" +
      "Datenschutzerklärung einfügen:\n\n" +
      "---- ANFANG TEXT-VORLAGE ----\n\n" +
      "KI-Sichtbarkeit (Shearly)\n\n" +
      "Wir nutzen den Dienst \"KI-Sichtbarkeit\" der Firma Shearly (Tobias Huber,\n" +
      "Fichtenstr. 2, 82041 Deisenhofen, Deutschland) um in KI-Suchmaschinen\n" +
      "wie ChatGPT, Perplexity, Google AI und Claude auffindbar zu sein.\n\n" +
      "Beim Aufruf unserer Website lädt Ihr Browser eine kleine JavaScript-Datei\n" +
      "von shearly.pro nach. Dabei wird Ihre IP-Adresse, Ihr User-Agent und der\n" +
      "Referer an Shearly übertragen und im dortigen Server-Zugriffsprotokoll\n" +
      "gespeichert (Aufbewahrung 30 Tage). Es werden keine Cookies gesetzt,\n" +
      "kein Tracking betrieben und keine personenbezogenen Profile gebildet.\n\n" +
      "Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse,\n" +
      "in KI-Suchmaschinen auffindbar zu sein).\n\n" +
      "Auftragsverarbeitung: Mit Shearly besteht ein Auftragsverarbeitungs-\n" +
      "vertrag gemäß Art. 28 DSGVO.\n\n" +
      "Widerspruchsrecht: Sie können der Verarbeitung jederzeit widersprechen\n" +
      "(Art. 21 DSGVO) — z.B. durch Skript-Blocker oder Browser-Einstellungen.\n\n" +
      "---- ENDE TEXT-VORLAGE ----\n\n" +
      "════════════════════════════════════════════\n" +
      "3) AUSFÜHRLICHE ANLEITUNG\n" +
      "════════════════════════════════════════════\n\n" +
      "Komplette Anleitung mit Plattform-Guides (Wordpress / Wix / Jimdo /\n" +
      "Squarespace / Webflow / eigene Website) und Test-Anleitung:\n" +
      "https://shearly.pro/webdev-guide\n\n" +
      "KURZ-INFO falls keine Zeit für die Anleitung:\n" +
      "• Was es macht: Lädt async Salon-Daten als JSON-LD in den <head>.\n" +
      "  Schema.org-HairSalon-Format.\n" +
      "• Datenschutz: Keine Cookies, kein Tracking, kein Profiling.\n" +
      "  ABER: Da der Snippet von shearly.pro nachgeladen wird, ist IP/User-Agent\n" +
      "  betroffen — deshalb bitte den Absatz aus Punkt 2 in die Datenschutzerklärung\n" +
      "  einfügen (DSGVO-konform).\n" +
      "• Performance: ~2 KB, async, blockiert nichts.\n" +
      "• Wartung: Keine — Salon-Daten werden serverseitig aktuell gehalten.\n\n" +
      "WO einbauen:\n" +
      "• Wordpress → Plugin 'Insert Headers and Footers' → Header-Bereich\n" +
      "• Wix → Einstellungen → Erweitert → Benutzerdefinierter Code (Head)\n" +
      "• Jimdo → Eigener Code / Head bearbeiten\n" +
      "• Squarespace → Settings → Advanced → Code Injection → Header\n" +
      "• Webflow → Project Settings → Custom Code → Head Code\n" +
      "• Eigene Website → direkt in den <head> des Layout-Templates\n\n" +
      "VERIFIZIEREN nach Einbau:\n" +
      "• Browser-Quellcode öffnen → nach 'application/ld+json' suchen\n" +
      "• Oder: https://search.google.com/test/rich-results → URL eingeben\n\n" +
      "Bei Fragen einfach antworten oder direkt an support@shearly.de.\n\n" +
      "Vielen Dank!\n"
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) return null;
  if (!data) return null;

  const score = data.score || 0;
  const checks = data.checks || {};
  const isLive = score >= 5;
  const isPartial = score >= 3 && score < 5;
  const noWebsite = !data.website_url;
  const isEnabled = !!data.enabled;

  // INAKTIV-TEASER (Add-on nicht gebucht) — ansehnlich, dezent, nicht nervig
  if (!isEnabled) {
    const requestActivation = () => {
      const subject = encodeURIComponent("KI-Sichtbarkeit aktivieren — " + (data.salon_name || ""));
      const body = encodeURIComponent(
        "Hallo Shearly-Team,\n\n" +
        "ich möchte das Add-on \"KI-Sichtbarkeit\" (Launch-Preis 9 EUR/Monat zzgl. MwSt. bis 30.06.2026) für meinen Salon\n" +
        "\"" + (data.salon_name || "") + "\" aktivieren.\n\n" +
        "Bitte um Rückmeldung.\n\nDanke!"
      );
      window.location.href = `mailto:support@shearly.de?subject=${subject}&body=${body}`;
    };
    return (
      <Card style={{ marginBottom: 16, padding: 0, overflow: "hidden", border: "1px solid var(--border)" }}>
        <div style={{
          padding: "16px 20px",
          background: "linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(167,139,250,0.06) 100%)",
          display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap"
        }}>
          {/* Plattform-Logos-Reihe */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: 6, background: "#0F0F0F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>GPT</div>
            <div style={{ width: 30, height: 30, borderRadius: 6, background: "#D97706", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>C</div>
            <div style={{ width: 30, height: 30, borderRadius: 6, background: "#1FB6FF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>P</div>
            <div style={{ width: 30, height: 30, borderRadius: 6, background: "#4285F4", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>G</div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>
              Add-on · Optional
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, lineHeight: 1.25 }}>
              Werde in ChatGPT, Perplexity &amp; Google AI gefunden
            </div>
            <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 3 }}>
              Automatische Salon-Daten für KI-Suchmaschinen — Launch-Preis 9 €/Monat zzgl. MwSt.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)",
                background: "var(--card)", color: COLORS.text,
                fontSize: 12, fontWeight: 600, cursor: "pointer"
              }}
            >
              {expanded ? "Weniger" : "Mehr Info"}
            </button>
            <button
              onClick={requestActivation}
              style={{
                padding: "8px 16px", borderRadius: 8, border: "1px solid " + COLORS.primary,
                background: COLORS.primary, color: "#fff",
                fontSize: 12, fontWeight: 700, cursor: "pointer"
              }}
            >
              Aktivieren
            </button>
          </div>
        </div>
        {expanded && (
          <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", fontSize: 12.5, lineHeight: 1.55, color: COLORS.text }}>
            <p style={{ margin: "0 0 8px" }}>
              Immer mehr Kunden suchen über <b>ChatGPT, Perplexity oder Google AI</b> nach {isKosmetikSalon() ? "Kosmetikstudios" : "Friseuren"} —
              statt zu googeln. Wer dort nicht auftaucht, verliert diese Kunden.
            </p>
            <p style={{ margin: "0 0 8px" }}>
              <b>Was du bekommst:</b>
            </p>
            <ul style={{ margin: "0 0 8px 18px", padding: 0, color: COLORS.textLight }}>
              <li>Strukturierte Salon-Daten (Services, Preise, Bewertungen, Öffnungszeiten) automatisch für KIs aufbereitet</li>
              <li>Sichtbarkeits-Score 0–5 mit Live-Check pro KI-Plattform</li>
              <li>Code-Snippet für deine Website — einmal einbauen, fertig</li>
              <li>Täglicher Recheck automatisch im Hintergrund</li>
              <li><i>Geplant Q4 2026:</i> „Dein Salon wurde diesen Monat in X KI-Antworten zitiert" (Citation-Tracking inklusive)</li>
            </ul>
            <div style={{
              background: "rgba(124,58,237,0.08)", padding: "10px 12px", borderRadius: 6,
              borderLeft: "3px solid " + COLORS.primary, fontSize: 12, marginBottom: 10
            }}>
              <b style={{ color: COLORS.primary }}>🚀 Launch-Preis bis 30.06.2026:</b><br/>
              <b>9 €/Monat zzgl. MwSt.</b> statt regulär 19 €. Wer jetzt aktiviert, behält die 9 € dauerhaft —
              auch nach Preiserhöhung am 1.7.2026.
            </div>
            <p style={{ margin: 0, color: COLORS.textLight }}>
              Jederzeit kündbar. Keine Setup-Kosten.<br/>
              <b>Aufwand für dich:</b> Code-Snippet einmal an deinen Webentwickler weiterleiten — 5 Minuten.
            </p>
          </div>
        )}
      </Card>
    );
  }
  // Ab hier: AKTIV-Variante (Premium-Card)

  const headerColor = isLive ? COLORS.success : (isPartial ? "#F59E0B" : COLORS.danger);
  const headerBg = isLive
    ? "linear-gradient(135deg, #059669 0%, #10B981 100%)"
    : "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)";

  // Plattform-Status — Backend lumpt Claude+Perplexity in einem Check
  const platforms = [
    { key: "gptbot_allowed",                name: "ChatGPT (OpenAI)",       desc: "OpenAI's GPTBot darf deine Seite lesen" },
    { key: "anthropic_perplexity_allowed",  name: "Claude + Perplexity",    desc: "ClaudeBot & PerplexityBot dürfen deine Seite lesen" },
    { key: "google_extended_allowed",       name: "Google AI Overviews",    desc: "Google-Extended (KI-Suche) darf deine Seite lesen" },
  ];

  return (
    <Card style={{ marginBottom: 16, padding: 0, overflow: "hidden" }}>
      {/* HEADER mit Verlauf */}
      <div style={{ background: headerBg, padding: "20px 24px", color: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.2, opacity: 0.85, textTransform: "uppercase", marginBottom: 4 }}>
              KI-Sichtbarkeit · NEU
            </div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>
              Dein Salon in ChatGPT, Perplexity &amp; Google AI
            </h2>
            <div style={{ fontSize: 13, marginTop: 6, opacity: 0.92 }}>
              {noWebsite
                ? "Trag deine Salon-Website unten ein, damit wir starten können."
                : isLive
                  ? "Vollständig sichtbar — KIs können deinen Salon empfehlen."
                  : isPartial
                    ? "Fast komplett — ein letzter Schritt fehlt noch."
                    : "Aktion nötig — KIs finden deinen Salon noch nicht."}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>
              {noWebsite ? <span style={{ fontSize: 22 }}>—</span> : <>{score}<span style={{ fontSize: 18, opacity: 0.7 }}>/5</span></>}
            </div>
            <div style={{ fontSize: 10, letterSpacing: 1, opacity: 0.85, marginTop: 4 }}>SICHTBARKEITS-SCORE</div>
          </div>
        </div>
        {/* Score-Bar — nur wenn Website hinterlegt */}
        {!noWebsite && (
          <div style={{ marginTop: 16, display: "flex", gap: 6 }}>
            {[0,1,2,3,4].map(i => (
              <div key={i} style={{
                flex: 1, height: 6, borderRadius: 3,
                background: i < score ? "#fff" : "rgba(255,255,255,0.25)"
              }} />
            ))}
          </div>
        )}
      </div>

      {/* BODY */}
      <div style={{ padding: "20px 24px" }}>
        {noWebsite && (
          <div style={{
            background: "#FEF3C7", border: "1px solid #F59E0B", borderRadius: 10,
            padding: 14, marginBottom: 16, fontSize: 13, color: "#92400E",
            cursor: editingUrl ? "default" : "pointer"
          }} onClick={() => { if (!editingUrl) { setEditingUrl(true); setUrlInput(""); setUrlError(""); } }}>
            {!editingUrl ? (
              <>
                <b>📍 Salon-Website noch nicht hinterlegt.</b><br/>
                <span style={{ fontSize: 12 }}>
                  Damit deine KI-Sichtbarkeit gemessen werden kann, brauchen wir die Adresse deiner Salon-Website
                  (z.B. <code style={{ background: "rgba(0,0,0,0.06)", padding: "1px 5px", borderRadius: 3 }}>https://www.dein-salon.de</code>).
                  &nbsp;<u style={{ fontWeight: 700 }}>Hier klicken zum Eintragen →</u>
                </span>
              </>
            ) : (
              <div onClick={(e) => e.stopPropagation()}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                  Adresse deiner Salon-Website
                </div>
                <input
                  type="url"
                  autoFocus
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveWebsite(); if (e.key === "Escape") setEditingUrl(false); }}
                  placeholder="https://www.dein-salon.de"
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 6,
                    border: "1px solid #92400E", fontSize: 13, color: "#92400E",
                    background: "#fff", marginBottom: 8, boxSizing: "border-box"
                  }}
                />
                {urlError && <div style={{ fontSize: 11, color: COLORS.danger, marginBottom: 8 }}>{urlError}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={saveWebsite}
                    disabled={savingUrl}
                    style={{
                      padding: "8px 16px", borderRadius: 6, border: "none",
                      background: "#92400E", color: "#fff", fontSize: 12, fontWeight: 700,
                      cursor: savingUrl ? "wait" : "pointer", opacity: savingUrl ? 0.6 : 1
                    }}
                  >
                    {savingUrl ? "Speichere..." : "Speichern & prüfen"}
                  </button>
                  <button
                    onClick={() => { setEditingUrl(false); setUrlError(""); }}
                    style={{
                      padding: "8px 14px", borderRadius: 6,
                      border: "1px solid #92400E", background: "transparent",
                      color: "#92400E", fontSize: 12, fontWeight: 600, cursor: "pointer"
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
                <div style={{ fontSize: 10, color: "#92400E", marginTop: 8, opacity: 0.8 }}>
                  Tipp: Die URL deiner öffentlichen Salon-Website. Falls du noch keine hast, brauchst du eine — KI-Suchmaschinen lesen Daten von dort.
                </div>
              </div>
            )}
          </div>
        )}
        {/* AI_VISIBILITY_COMPLETED_HIDE_v1 -- Kompakt-Box wenn alles live */}
        {isLive && !showDetailsWhenLive && (
          <div style={{
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.35)",
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 16,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 10
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 17,
                background: COLORS.success, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 800, flexShrink: 0
              }}>✓</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
                  Alles eingerichtet — KIs finden deinen Salon
                </div>
                <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2 }}>
                  Snippet eingebaut, alle KI-Plattformen freigeschaltet. Tägliche Prüfung läuft automatisch.
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleRecheck}
                disabled={rechecking}
                title="Status erneut prüfen"
                style={{
                  padding: "7px 12px", borderRadius: 6, border: "1px solid var(--border)",
                  background: "var(--card)", color: COLORS.text,
                  fontSize: 12, fontWeight: 600, cursor: rechecking ? "wait" : "pointer",
                  opacity: rechecking ? 0.6 : 1
                }}
              >
                {rechecking ? "Prüfe..." : "🔄 Erneut prüfen"}
              </button>
              <button
                onClick={() => setShowDetailsWhenLive(true)}
                style={{
                  padding: "7px 12px", borderRadius: 6, border: "1px solid var(--border)",
                  background: "var(--card)", color: COLORS.textLight,
                  fontSize: 12, fontWeight: 600, cursor: "pointer"
                }}
              >
                Details anzeigen
              </button>
            </div>
          </div>
        )}
        {/* Plattform-Liste — nur sinnvoll wenn Website hinterlegt UND noch nicht fertig (oder Details aufgeklappt) */}
        {!noWebsite && (!isLive || showDetailsWhenLive) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Status pro KI-Plattform
            </div>
            {isLive && showDetailsWhenLive && (
              <button
                onClick={() => setShowDetailsWhenLive(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 11, color: COLORS.textLight, fontWeight: 600, padding: 0
                }}
              >
                Details einklappen ▲
              </button>
            )}
          </div>
          {platforms.map(p => {
            const ok = !!checks[p.key];
            return (
              <div key={p.key} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 11,
                  background: ok ? COLORS.success + "22" : COLORS.danger + "22",
                  color: ok ? COLORS.success : COLORS.danger,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 700, marginRight: 12, flexShrink: 0
                }}>{ok ? "✓" : "✗"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textLight }}>{p.desc}</div>
                </div>
              </div>
            );
          })}
          {/* Embed-Snippet als 5. Zeile */}
          <div style={{ display: "flex", alignItems: "center", padding: "8px 0" }}>
            <div style={{
              width: 22, height: 22, borderRadius: 11,
              background: checks.embed_installed ? COLORS.success + "22" : "#F59E0B22",
              color: checks.embed_installed ? COLORS.success : "#F59E0B",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 700, marginRight: 12, flexShrink: 0
            }}>{checks.embed_installed ? "✓" : "!"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                Salon-Daten-Snippet auf deiner Website
              </div>
              <div style={{ fontSize: 11, color: COLORS.textLight }}>
                {checks.embed_installed
                  ? "Snippet eingebaut — KIs sehen deine Services, Öffnungszeiten, Bewertungen."
                  : "Snippet noch nicht eingebaut — siehe Embed-Code unten."}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* CTA-Box wenn nicht 5/5 und Website hinterlegt */}
        {!isLive && !noWebsite && (
          <div style={{
            background: "var(--cardHover)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            padding: 16,
            marginBottom: 16
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
              {checks.embed_installed === false && score >= 4
                ? "🎯 Letzter Schritt: Code-Snippet einbauen"
                : "🎯 So machen wir deinen Salon KI-sichtbar"}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textLight, lineHeight: 1.5 }}>
              {checks.embed_installed === false
                ? <>Du oder dein Webentwickler kopiert <b>einmalig</b> den Code-Snippet unten in den <code style={{ background: "var(--card)", padding: "1px 5px", borderRadius: 3, fontSize: 11 }}>&lt;head&gt;</code>-Bereich deiner Website. Dauert keine 5 Minuten.</>
                : <>Deine robots.txt blockiert noch KI-Bots. Frag deinen Webentwickler, ob er die Sperre für GPTBot, ClaudeBot, PerplexityBot und Google-Extended entfernt.</>}
            </div>
          </div>
        )}

        {/* WARUM-WICHTIG-BLOCK (Story) — bei isLive ausblenden */}
        {(!isLive || showDetailsWhenLive) && (
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 13, fontWeight: 700, color: COLORS.primary, padding: 0
            }}
          >
            {expanded ? "▼" : "▶"} Warum ist das wichtig?
          </button>
          {expanded && (
            <div style={{
              marginTop: 10, padding: 14, background: "var(--cardHover)",
              borderRadius: 8, fontSize: 12.5, lineHeight: 1.6, color: COLORS.text
            }}>
              <p style={{ margin: "0 0 10px" }}>
                Immer mehr Kunden fragen <b>ChatGPT</b>, <b>Perplexity</b> oder <b>Google AI</b> statt zu googeln:<br/>
                <i>{isKosmetikSalon() ? <>„Welches Kosmetikstudio in {data.city || "meiner Stadt"} ist gut für Microneedling?"</> : <>„Welcher Friseur in {data.city || "meiner Stadt"} ist gut für Balayage?"</>}</i>
              </p>
              <p style={{ margin: "0 0 10px" }}>
                Diese KIs lesen Webseiten und Salon-Daten und <b>empfehlen dann konkrete Salons</b> — mit Name, Adresse, Bewertungen, Services.
                Wer von der KI nicht gefunden wird, verliert diese Kunden komplett: sie sehen deine Konkurrenz, nicht dich.
              </p>
              <p style={{ margin: "0 0 12px" }}>
                Mit dem Code-Snippet bekommen die KIs strukturierte Daten über deinen Salon — alle Services mit Preisen,
                Öffnungszeiten, Adresse, Bewertungen, Spezialitäten. Du wirst Teil der KI-Antworten, ohne tägliche Pflege.
                Die Salon-Daten werden automatisch aus deinem Shearly-System gezogen und immer aktuell gehalten.
              </p>
              <div style={{
                background: "rgba(124,58,237,0.08)", padding: "8px 10px", borderRadius: 6,
                fontSize: 11.5, color: COLORS.text, borderLeft: "2px solid " + COLORS.primary
              }}>
                🚀 <b>Geplant Q4 2026:</b> Du siehst monatlich, in wie vielen KI-Antworten dein Salon zitiert wurde.
                Beispiel: „Dein Salon wurde im Oktober in 47 Perplexity-Antworten erwähnt." Inklusive im Add-on.
              </div>
            </div>
          )}
        </div>
        )}

        {/* EMBED-CODE-BOX — nur sinnvoll wenn Website hinterlegt UND nicht schon fertig */}
        {data.embed_snippet && !noWebsite && (!isLive || showDetailsWhenLive) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
              Code-Snippet für deinen Webentwickler
            </div>
            {/* EMBED_SNIPPET_DISPLAY_v2 -- vorher: <div> ohne white-space → \n verschwand → Kommentar und Script
                wurden zur Textwurst; wordBreak:break-all hat URLs mitten zerrissen. Jetzt als <pre>:
                echte Zeilenumbrüche, monospace bleibt zusammen, horizontaler Scroll wenn nötig. */}
            <pre style={{
              background: "var(--cardHover)", border: "1px solid var(--border)",
              borderRadius: 8, padding: 12, fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
              fontSize: 11.5, color: COLORS.text, lineHeight: 1.5,
              maxHeight: 120, overflow: "auto",
              whiteSpace: "pre", wordBreak: "normal",
              margin: 0,
            }}>
              {data.embed_snippet}
            </pre>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button
                onClick={copyEmbed}
                style={{
                  padding: "8px 14px", borderRadius: 8, border: "1px solid " + COLORS.primary,
                  background: copied ? COLORS.success : COLORS.primary, color: "#fff",
                  fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex",
                  alignItems: "center", gap: 6
                }}
              >
                {copied ? "✓ Kopiert!" : "📋 Code kopieren"}
              </button>
              <button
                onClick={mailToWebdev}
                style={{
                  padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)",
                  background: "var(--card)", color: COLORS.text,
                  fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex",
                  alignItems: "center", gap: 6
                }}
              >
                ✉️ An Webentwickler senden
              </button>
              <button
                onClick={handleRecheck}
                disabled={rechecking}
                style={{
                  padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)",
                  background: "var(--card)", color: COLORS.text,
                  fontSize: 12, fontWeight: 700, cursor: rechecking ? "wait" : "pointer",
                  opacity: rechecking ? 0.6 : 1, display: "inline-flex", alignItems: "center", gap: 6
                }}
              >
                🔄 {rechecking ? "Prüfe..." : "Erneut prüfen"}
              </button>
            </div>
          </div>
        )}

        {/* DSGVO-Textbaustein für Owner — bei isLive ausblenden (wurde schon kopiert) */}
        {!noWebsite && data.embed_snippet && (!isLive || showDetailsWhenLive) && (
          <div style={{
            background: "rgba(59,130,246,0.06)", borderLeft: "3px solid " + COLORS.info,
            borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 12.5, lineHeight: 1.55
          }}>
            <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
              📋 Datenschutz-Hinweis für deine Website
            </div>
            <div style={{ color: COLORS.textLight, marginBottom: 8 }}>
              Sobald das Snippet eingebaut ist, lädt deine Website etwas von Shearly nach.
              Daher braucht deine eigene Datenschutzerklärung einen kleinen Zusatz.
              Wir haben dir den fertigen Text vorbereitet — einmal kopieren, deinem Webentwickler zum
              Einbauen schicken.
            </div>
            <button
              onClick={() => {
                const text = `KI-Sichtbarkeit (Shearly)

Wir nutzen den Dienst "KI-Sichtbarkeit" der Firma Shearly (Tobias Huber, Fichtenstr. 2, 82041 Deisenhofen, Deutschland) um in KI-Suchmaschinen wie ChatGPT, Perplexity, Google AI und Claude auffindbar zu sein.

Beim Aufruf unserer Website lädt Ihr Browser eine kleine JavaScript-Datei von shearly.pro nach. Dabei wird Ihre IP-Adresse, Ihr User-Agent und der Referer an Shearly übertragen und im dortigen Server-Zugriffsprotokoll gespeichert (Aufbewahrung 30 Tage). Es werden keine Cookies gesetzt, kein Tracking betrieben und keine personenbezogenen Profile gebildet.

Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse, in KI-Suchmaschinen auffindbar zu sein).

Auftragsverarbeitung: Mit Shearly besteht ein Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO.

Widerspruchsrecht: Sie können der Verarbeitung jederzeit widersprechen (Art. 21 DSGVO) — z.B. durch Skript-Blocker oder Browser-Einstellungen.`;
                navigator.clipboard.writeText(text).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2500);
                });
              }}
              style={{
                padding: "7px 12px", borderRadius: 6, border: "1px solid " + COLORS.info,
                background: "var(--card)", color: COLORS.info,
                fontSize: 12, fontWeight: 700, cursor: "pointer"
              }}
            >
              📋 Datenschutz-Text kopieren
            </button>
            <a
              href="https://shearly.pro/webdev-guide"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: 8, fontSize: 11.5, color: COLORS.info, textDecoration: "none",
                fontWeight: 600
              }}
            >
              Webdev-Guide ansehen →
            </a>
          </div>
        )}

        {/* FAQ — bei isLive ausblenden */}
        {(!isLive || showDetailsWhenLive) && (
        <div>
          <button
            onClick={() => setShowFaq(f => !f)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 13, fontWeight: 700, color: COLORS.primary, padding: 0
            }}
          >
            {showFaq ? "▼" : "▶"} Häufige Fragen
          </button>
          {showFaq && (
            <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.6 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
                  Muss ich das selbst auf der Website einbauen?
                </div>
                <div style={{ color: COLORS.textLight }}>
                  Nein. Du sendest den Code-Snippet einfach an deinen Webentwickler oder Website-Anbieter
                  (z.B. Wordpress, Wix, Jimdo). Ein Klick auf <b>„An Webentwickler senden"</b> öffnet eine fertige E-Mail.
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
                  Was kostet das?
                </div>
                <div style={{ color: COLORS.textLight }}>
                  <b>9 €/Monat zzgl. MwSt.</b> Launch-Preis bis 30.06.2026 (regulär 19 €/Monat).
                  Wer jetzt aktiviert, zahlt dauerhaft 9 € — auch nach der Preiserhöhung. Jederzeit kündbar, keine Setup-Kosten.
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
                  Wie lange dauert es bis ich in ChatGPT auftauche?
                </div>
                <div style={{ color: COLORS.textLight }}>
                  Sobald der Code drin ist, lesen die KI-Bots beim nächsten Crawl deine Daten ein.
                  Erste Sichtbarkeit meist nach 1–4 Wochen, voll etabliert nach 1–2 Monaten.
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
                  Was wird konkret von der KI gelesen?
                </div>
                <div style={{ color: COLORS.textLight }}>
                  Salon-Name, Adresse, Telefon, Öffnungszeiten, alle aktiven Services mit Preisen, durchschnittliche Bewertung.
                  Alles automatisch aus deinem Shearly-System — du musst nichts pflegen.
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Letzter Check */}
        {data.last_check && (
          <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 16, textAlign: "right" }}>
            Letzter Check: {new Date(data.last_check).toLocaleString("de-DE")}
          </div>
        )}
      </div>
    </Card>
  );
};

export const DashboardPage = ({ appointments, customers, employees, services, canSeeFinancials, enabledFeatures, onGoToPayments }) => {
  const todayStr = formatDate(today);
  const todayAppts = appointments.filter(a => a.date === todayStr);
  const todayRevenue = todayAppts.reduce((s, a) => s + a.totalPrice, 0);
  const serviceBreakdown = useMemo(() => {
    const map = {};
    todayAppts.forEach(a => {
      a.serviceIds.forEach(sid => {
        const svc = services.find(s => s.id === sid);
        if (svc) map[svc.category] = (map[svc.category] || 0) + 1;
      });
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [todayAppts, services]);

  const PIE_COLORS = ["#7C3AED", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899"];

  // DASHBOARD_REAL_v1 (A) -- echter Wochenumsatz (Mo-So der laufenden Woche) statt Demo-Konstante
  const weekRevenue = useMemo(() => {
    const monday = new Date(today);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    return ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((day, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const ds = formatDate(d);
      const dayAppts = appointments.filter(a => a.date === ds && a.status !== "cancelled");
      return { day, revenue: dayAppts.reduce((s, a) => s + (a.totalPrice || 0), 0), bookings: dayAppts.length };
    });
  }, [appointments]);

  // ============ DASHBOARD_TILES_v1 -- 8 neue Echt-Daten-Kacheln (Toby-GO 14.07.) ============
  // Alles NUR lesend, keine Buchungs-/Slot-Logik beruehrt. Belegungs-Rechnung = exakt die
  // Block/Luecken-Logik der Monatsuebersicht (OCC_MINI_TIMELINE_v1); Schichten frisch via API.
  const [dashShifts, setDashShifts] = useState([]);
  const [shiftsLoaded, setShiftsLoaded] = useState(false);
  const [dashGoogle, setDashGoogle] = useState(null);
  const [dashAiCalls, setDashAiCalls] = useState(null);
  // DASHBOARD_SORT_v1 -- welcher der drei kompakten Info-Bloecke ist aufgeklappt (null = keiner)
  const [openInfoWidget, setOpenInfoWidget] = useState(null);
  const aiModulOn = !!(enabledFeatures && enabledFeatures.ai_telephony === true);
  useEffect(() => {
    let alive = true;
    // api.request liefert {ok,status,data}, nie ein nacktes Array (OCC_SHIFTS_FETCH_FIX-Lehre)
    api.request("/shifts").then(r => { if (alive && r && r.ok && Array.isArray(r.data)) { setDashShifts(r.data); setShiftsLoaded(true); } }).catch(() => {});
    if (canSeeFinancials) api.request("/salon/google-settings").then(r => { if (alive && r && r.ok && r.data && !r.data.error) setDashGoogle(r.data); }).catch(() => {});
    if (aiModulOn) api.request("/ai-calls").then(r => { if (alive && r && r.ok && Array.isArray(r.data)) setDashAiCalls(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [aiModulOn]);

  const occToMin = (t) => { const p = String(t || "").split(":"); return (Number(p[0]) || 0) * 60 + (Number(p[1]) || 0); };
  const occFmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(Math.round(m) % 60).padStart(2, "0")}`;
  const occDayKey = (d) => ["so", "mo", "di", "mi", "do", "fr", "sa"][d.getDay()];
  // BIWEEKLY_SHIFT_v1 -- ds optional: 14-Tage-Zeilen zählen nur in ihrer Woche, paritäts-spezifische Zeile gewinnt
  const occWeekShift = (empId, dk, ds) => {
    const rows = dashShifts.filter(s => s.employee_id === empId && s.day_of_week === dk && !s.is_off && s.start_time && s.end_time && (!ds || shiftAppliesOn(s, ds)));
    return rows.find(s => s.week_parity != null) || rows[0];
  };
  // Belegungs-Kennzahlen eines Tages (aggregiert + je Mitarbeiter): nur Mitarbeiter mit
  // Wochenschicht oder Terminen an dem Tag; ohne Schicht = Fenster erster-bis-letzter Termin.
  const occDayStats = (dayDate) => {
    const ds = formatDate(dayDate);
    const dk = occDayKey(dayDate);
    let availSum = 0, busySum = 0;
    const perEmp = [];
    employees.forEach(emp => {
      const sh = occWeekShift(emp.id, dk, ds);
      const dayAppts = appointments
        .filter(a => a.employeeId === emp.id && a.date === ds && a.status !== "cancelled" && !(a.isPhaseMaster || a.is_phase_master))
        .map(a => ({ s: occToMin(a.startTime), e: occToMin(a.startTime) + (a.totalDuration || 0) }))
        .sort((x, y) => x.s - y.s);
      if (!sh && dayAppts.length === 0) return;
      const winStart = sh ? occToMin(sh.start_time) : dayAppts[0].s;
      const winEnd = sh ? occToMin(sh.end_time) : dayAppts[dayAppts.length - 1].e;
      const span = Math.max(1, winEnd - winStart);
      const blocks = dayAppts.map(a => ({ ...a, type: "appt" }));
      if (sh && sh.break_start && sh.break_end) blocks.push({ s: occToMin(sh.break_start), e: occToMin(sh.break_end), type: "break" });
      blocks.sort((a, b) => a.s - b.s);
      const gaps = [];
      let cursor = winStart;
      blocks.forEach(b => {
        const bs = Math.max(b.s, winStart), be = Math.min(b.e, winEnd);
        if (bs > cursor) gaps.push([cursor, bs]);
        cursor = Math.max(cursor, be);
      });
      if (cursor < winEnd) gaps.push([cursor, winEnd]);
      const breakMin = blocks.filter(b => b.type === "break").reduce((s, b) => s + Math.max(0, Math.min(b.e, winEnd) - Math.max(b.s, winStart)), 0);
      const freeMin = gaps.reduce((s, g) => s + (g[1] - g[0]), 0);
      const availEff = Math.max(0, span - breakMin);
      availSum += availEff;
      busySum += Math.max(0, availEff - freeMin);
      perEmp.push({ emp, sh, gaps, freeMin });
    });
    const pct = availSum > 0 ? Math.min(100, Math.round((busySum / availSum) * 100)) : null;
    return { pct, perEmp, availSum, busySum };
  };
  const occToday = useMemo(() => occDayStats(today), [appointments, dashShifts, employees]);
  const occWeekPct = useMemo(() => {
    const monday = new Date(today);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    let avail = 0, busy = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const st = occDayStats(d);
      avail += st.availSum; busy += st.busySum;
    }
    return avail > 0 ? Math.min(100, Math.round((busy / avail) * 100)) : null;
  }, [appointments, dashShifts, employees]);

  // Luecken heute (nur Mitarbeiter MIT Schichtplan, ab jetzt, mind. 15 Min) + naechster freier Slot
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const gapsToday = useMemo(() => {
    const list = [];
    occToday.perEmp.forEach(({ emp, sh, gaps }) => {
      if (!sh) return;
      gaps.forEach(g => {
        const s = Math.max(g[0], nowMin);
        if (g[1] - s >= 15) list.push({ emp, s, e: g[1] });
      });
    });
    return list.sort((a, b) => a.s - b.s);
  }, [occToday]);

  // Neue Online-Buchungen seit gestern, Quelle gekennzeichnet (Formular/Zoey/KI-Telefon).
  // Zoey-Buchungen VOR dem 14.07.2026 tragen noch source='online' und zaehlen als Formular.
  const SOURCE_LABELS = { online: "Online-Formular", zoey: "Zoey-Chat", ai_call: "KI-Telefon" };
  const newOnlineBookings = useMemo(() => {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    const cutoff = formatDate(y);
    return appointments.filter(a =>
      a && (a.source === "online" || a.source === "zoey" || a.source === "ai_call") &&
      a.status !== "cancelled" &&
      typeof a.createdAt === "string" && a.createdAt.slice(0, 10) >= cutoff
    ).sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  }, [appointments]);

  // Offene To-dos: unbestaetigte Termine (ab heute) + Mitarbeiter ohne Wochenschicht
  const pendingAppts = useMemo(() => appointments.filter(a => a && a.status === "pending" && a.date >= todayStr), [appointments]);
  const empsWithoutShift = useMemo(() => {
    if (!shiftsLoaded) return [];
    return employees.filter(emp => !dashShifts.some(s => s.employee_id === emp.id && !s.is_off && s.start_time && s.end_time));
  }, [employees, dashShifts, shiftsLoaded]);
  // NO_RECEIPT_CHECK_v1 -- Termine der letzten 7 Tage ohne Bon aus der Shearly Kasse (nur Inhaber/Admin,
  // nur Salons mit Kasse; Regel zentral in core/config.jsx)
  const posShearly = !!(enabledFeatures && enabledFeatures.pos_shearly === true);
  const noReceiptAppts = useMemo(
    () => (canSeeFinancials && posShearly) ? appointmentsWithoutReceipt(appointments, 7).items : [],
    [appointments, canSeeFinancials, posShearly]);

  // Kunden-Rueckgewinnung: letzter Besuch laenger als 8 Wochen her (juengst Verlorene zuerst)
  const winbackCustomers = useMemo(() => {
    const cut = new Date(today); cut.setDate(cut.getDate() - 56);
    const cutoff = formatDate(cut);
    return customers
      .filter(c => c && (c.visits || 0) > 0 && typeof c.lastVisit === "string" && c.lastVisit.length >= 10 && c.lastVisit.slice(0, 10) < cutoff)
      .sort((a, b) => String(b.lastVisit || "").localeCompare(String(a.lastVisit || "")));
  }, [customers]);

  // Neukunden + Top-Services/Top-Mitarbeiter im laufenden Monat
  const monthKey = todayStr.slice(0, 7);
  const newCustomersMonth = useMemo(() => customers.filter(c => c && typeof c.createdAt === "string" && c.createdAt.slice(0, 7) === monthKey).length, [customers]);
  const monthTop = useMemo(() => {
    const skip = { blocker: 1, phase: 1, vacation_auto: 1, assistant: 1 };
    const monthAppts = appointments.filter(a => a && a.date && a.date.slice(0, 7) === monthKey && a.status !== "cancelled" && !skip[a.source]);
    const svcMap = {}, empMap = {};
    monthAppts.forEach(a => {
      (a.serviceNames || []).forEach(n => { if (n) svcMap[n] = (svcMap[n] || 0) + 1; });
      const emp = employees.find(e => e.id === a.employeeId);
      if (emp) empMap[emp.name] = (empMap[emp.name] || 0) + 1;
    });
    const top = m => Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 3);
    return { services: top(svcMap), employees: top(empMap), total: monthAppts.length };
  }, [appointments, employees]);

  // Echte Retell-Zahlen (nur Salons mit KI-Modul; /api/ai-calls = echte Webhook-Daten)
  const aiStats = useMemo(() => {
    if (!Array.isArray(dashAiCalls)) return null;
    const todayCalls = dashAiCalls.filter(c => c && typeof c.created_at === "string" && c.created_at.slice(0, 10) === todayStr);
    return {
      today: todayCalls.length,
      booked: todayCalls.filter(c => c.result === "booked").length,
      recent: dashAiCalls.slice(0, 3),
    };
  }, [dashAiCalls]);
  // ============ Ende DASHBOARD_TILES_v1-Datenlogik ============

  return (
    <div>
      <Header title="Dashboard" subtitle={`${today.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`} />
      {/* DASHBOARD_SORT_v1 (Toby 15.07.) -- Wichtigstes nach oben: die drei grossen Info-Bloecke
          (KI-Sichtbarkeit, KI-Buchungs-Chat, SMS) sind nach unten in eine schmale, aufklappbare
          Zeile gewandert; der Umsatz-Wochen-Chart steht jetzt ganz unten. */}
      {/* DASHBOARD_REAL_v1 (B) -- Fake-Trends + hardcodierte KI-Anrufe-Kachel entfernt, nur noch echte Zahlen */}
      {/* DASHBOARD_TILES_v1 -- Raster auf auto-fill erweitert (mehr Kacheln, bleibt responsiv) */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(170px, 1fr))", gap: window.innerWidth < 768 ? 8 : 16, marginBottom: window.innerWidth < 768 ? 16 : 24 }}>
        {canSeeFinancials && <StatCard icon={Euro} label="Umsatz heute" value={`${todayRevenue} €`} color={COLORS.success} />}
        <StatCard icon={Calendar} label="Termine heute" value={todayAppts.length} color={COLORS.primary} />
        <StatCard icon={Users} label="Kunden gesamt" value={customers.length} color={COLORS.info} />
        <StatCard icon={Activity} label="Auslastung heute" value={occToday.pct === null ? "—" : `${occToday.pct} %`} color={COLORS.primary} />
        <StatCard icon={TrendingUp} label="Auslastung Woche" value={occWeekPct === null ? "—" : `${occWeekPct} %`} color={COLORS.info} />
        <StatCard icon={UserPlus} label="Neukunden (Monat)" value={newCustomersMonth} color={COLORS.success} />
        {canSeeFinancials && <StatCard icon={Star} label="Google-Bewertung" value={dashGoogle && dashGoogle.google_rating != null ? `${dashGoogle.google_rating} ★ (${dashGoogle.google_review_count || 0})` : "—"} color={COLORS.warning} />}
      </div>

      {/* DASHBOARD_SORT_v1 -- Charts (Umsatz Woche + Services heute) nach GANZ unten verschoben,
          Geburtstage-14-Tage-Karte hinter die Detail-Karten geschoben (Wichtigstes zuerst) */}

                        {/* DASHBOARD_TILES_v1 -- vier Detail-Karten mit echten Zahlen */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Neue Online-Buchungen <span style={{ fontWeight: 500, color: COLORS.textLight, fontSize: 12 }}>seit gestern</span></h3>
          {newOnlineBookings.length === 0 ? (
            <div style={{ padding: "16px 0", color: COLORS.textLight, fontSize: 13 }}>Keine neuen Online-Buchungen seit gestern.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                {Object.entries(SOURCE_LABELS).map(([key, label]) => {
                  const n = newOnlineBookings.filter(a => a.source === key).length;
                  if (n === 0) return null;
                  return <span key={key} style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12, background: `${COLORS.primary}12`, color: COLORS.primary }}>{label}: {n}</span>;
                })}
              </div>
              {newOnlineBookings.slice(0, 5).map(a => (
                <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{a.customerName || "—"}</span>
                  <span style={{ color: COLORS.textLight, fontSize: 12 }}>{SOURCE_LABELS[a.source] || a.source} · Termin {a.date ? a.date.split("-").reverse().join(".") : "—"} {a.startTime || ""}</span>
                </div>
              ))}
              {newOnlineBookings.length > 5 && <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>+{newOnlineBookings.length - 5} weitere</div>}
            </div>
          )}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Offene To-dos</h3>
          {pendingAppts.length === 0 && empsWithoutShift.length === 0 && noReceiptAppts.length === 0 ? (
            <div style={{ padding: "16px 0", color: COLORS.success, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle size={16} /> Alles erledigt — keine offenen Punkte.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {pendingAppts.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <AlertCircle size={16} color={COLORS.warning} />
                  <span><strong>{pendingAppts.length}</strong> unbestätigte{pendingAppts.length === 1 ? "r Termin" : " Termine"} (nächster: {pendingAppts.slice().sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime))[0].date.split("-").reverse().join(".")})</span>
                </div>
              )}
              {empsWithoutShift.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <AlertCircle size={16} color={COLORS.danger} />
                  <span>Ohne Schichtplan: <strong>{empsWithoutShift.map(e => e.name).slice(0, 4).join(", ")}{empsWithoutShift.length > 4 ? ` +${empsWithoutShift.length - 4}` : ""}</strong></span>
                </div>
              )}
              {noReceiptAppts.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, flexWrap: "wrap" }}>
                  <AlertCircle size={16} color={COLORS.warning} />
                  <span><strong>{noReceiptAppts.length}</strong> {noReceiptAppts.length === 1 ? "Termin" : "Termine"} der letzten 7 Tage ohne Bon aus der Kasse</span>
                  {onGoToPayments && (
                    <button onClick={onGoToPayments} style={{ background: "none", border: "none", padding: 0, color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>ansehen</button>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Lücken heute <span style={{ fontWeight: 500, color: COLORS.textLight, fontSize: 12 }}>ab jetzt</span></h3>
          {gapsToday.length === 0 ? (
            <div style={{ padding: "16px 0", color: COLORS.textLight, fontSize: 13 }}>{occToday.perEmp.some(p => p.sh) ? "Heute keine freien Lücken mehr — ausgebucht." : "Heute ist niemand im Schichtplan eingeteilt."}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 13, marginBottom: 4 }}>Nächster freier Slot: <strong>{occFmt(gapsToday[0].s)} Uhr bei {gapsToday[0].emp.name}</strong></div>
              {gapsToday.slice(0, 5).map((g, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: g.emp.color }} />{g.emp.name}</span>
                  <span style={{ color: COLORS.textLight }}>{occFmt(g.s)}–{occFmt(g.e)} ({g.e - g.s} Min)</span>
                </div>
              ))}
              {gapsToday.length > 5 && <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>+{gapsToday.length - 5} weitere Lücken</div>}
            </div>
          )}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Top im {today.toLocaleDateString("de-DE", { month: "long" })} <span style={{ fontWeight: 500, color: COLORS.textLight, fontSize: 12 }}>{monthTop.total} Termine</span></h3>
          {monthTop.total === 0 ? (
            <div style={{ padding: "16px 0", color: COLORS.textLight, fontSize: 13 }}>Noch keine Termine in diesem Monat.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textLight, marginBottom: 6 }}>Top-Services</div>
                {monthTop.services.map(([name, n]) => (
                  <div key={name} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>{name}</span><strong>{n}×</strong>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textLight, marginBottom: 6 }}>Top-Mitarbeiter</div>
                {monthTop.employees.map(([name, n]) => (
                  <div key={name} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>{name}</span><strong>{n} Termine</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Kunden-Rückgewinnung <span style={{ fontWeight: 500, color: COLORS.textLight, fontSize: 12 }}>über 8 Wochen nicht da</span></h3>
          {winbackCustomers.length === 0 ? (
            <div style={{ padding: "16px 0", color: COLORS.success, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle size={16} /> Alle Kundinnen und Kunden waren kürzlich da.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 13, marginBottom: 4 }}><strong>{winbackCustomers.length}</strong> Kund{winbackCustomers.length === 1 ? "e" : "en"} länger nicht gesehen — vielleicht Zeit für eine Einladung?</div>
              {winbackCustomers.slice(0, 5).map(c => (
                <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{c.name}</span>
                  <span style={{ color: COLORS.textLight, fontSize: 12 }}>zuletzt {String(c.lastVisit).slice(0, 10).split("-").reverse().join(".")}</span>
                </div>
              ))}
              {winbackCustomers.length > 5 && <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>+{winbackCustomers.length - 5} weitere</div>}
            </div>
          )}
        </Card>
      </div>

      {/* DASHBOARD_TILES_v1 -- echte Retell-Karte, NUR fuer Salons mit KI-Modul (ai_telephony
          aus /api/features, Spalten ai_receptionist_enabled/ai_telephony_enabled). Ersetzt die
          in DASHBOARD_REAL_v1 (C) entfernte Fake-"Live Status"-Karte. Daten: /api/ai-calls (echt). */}
      {aiModulOn && (
        <Card style={{ marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}><Bot size={18} /> KI-Telefon heute</h3>
          {aiStats === null ? (
            <div style={{ padding: "8px 0", color: COLORS.textLight, fontSize: 13 }}>Lade Anrufdaten …</div>
          ) : (
            <div>
              <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 13 }}>
                <span><strong>{aiStats.today}</strong> Anruf{aiStats.today === 1 ? "" : "e"} heute</span>
                <span><strong>{aiStats.booked}</strong> davon gebucht</span>
              </div>
              {aiStats.recent.length === 0 ? (
                <div style={{ fontSize: 13, color: COLORS.textLight }}>Noch keine Anrufe aufgezeichnet.</div>
              ) : (
                aiStats.recent.map(c => (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                    <span>{c.caller_name || c.caller_phone || "Unbekannt"}</span>
                    <span style={{ color: COLORS.textLight, fontSize: 12 }}>{String(c.created_at || "").slice(0, 16).replace("T", " ")} · {c.result === "booked" ? "Termin gebucht" : c.result === "info" ? "Auskunft" : c.result === "escalated" ? "Weitergeleitet" : c.result || "—"}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      )}

      {/* DASHBOARD_SORT_v1 -- Geburtstage-14-Tage-Karte hierher verschoben (unveraendert) */}
                        <Card style={{ marginBottom: 24 }}>
                            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                                🎂 Geburtstage nächste 14 Tage
                            </h3>
                            {(() => {
                                const today = new Date(); today.setHours(0,0,0,0);
                                const y = today.getFullYear();
                                const up = (customers || []).filter(c => c.birthday && c.birthday.length >= 10).map(c => {
                                    const parts = c.birthday.split('-').map(Number);
                                    const by = parts[0], bm = parts[1], bd = parts[2];
                                    let next = new Date(y, bm - 1, bd);
                                    if (next < today) next = new Date(y + 1, bm - 1, bd);
                                    const days = Math.round((next - today) / 86400000);
                                    return Object.assign({}, c, { _days: days, _age: next.getFullYear() - by });
                                }).filter(x => x._days >= 0 && x._days <= 14).sort((a,b) => a._days - b._days).slice(0, 8);
                                return up.length === 0 ?
                                    <div style={{ textAlign: "center", padding: 24, color: COLORS.textLight }}>Keine Geburtstage in den nächsten 14 Tagen</div>
                                    :
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {up.map(c => (
                                            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: c._days === 0 ? "rgba(254,243,199,0.15)" : "var(--cardHover)", border: c._days === 0 ? "1px solid rgba(180,83,9,0.3)" : `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                                                    <div style={{ fontSize: 12, color: COLORS.textLight }}>wird {c._age} • {c.birthday.slice(5)}</div>
                                                </div>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: c._days === 0 ? "#B45309" : COLORS.textLight }}>
                                                    {c._days === 0 ? "🎉 Heute!" : c._days === 1 ? "Morgen" : `in ${c._days} Tagen`}
                                                </div>
                                            </div>
                                        ))}
                                    </div>;
                            })()}
                        </Card>

      <Card>
        <Card style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <h3 style={{margin:0,fontSize:15,fontWeight:700}}>Geburtstage diese Woche</h3>
              </div>
              {(() => {
                const today = new Date();
                const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
                const bdays = customers.filter(c => {
                  if (!c.birthday) return false;
                  const bd = new Date(c.birthday);
                  const thisYear = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
                  return thisYear >= today && thisYear <= weekEnd;
                });
                if (bdays.length === 0) return <p style={{color:COLORS.textLight,fontSize:13}}>Keine Geburtstage diese Woche</p>;
                return bdays.map(c => <div key={c.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+COLORS.border,fontSize:13}}>
                  <span>{c.name}</span>
                  <span style={{color:COLORS.textLight}}>{new Date(c.birthday).toLocaleDateString("de-DE",{day:"numeric",month:"short"})}</span>
                </div>);
              })()}
            </Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Heutige Termine</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {["Zeit", "Kunde", "Mitarbeiter", "Services", "Preis", "Status"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {todayAppts.sort((a, b) => a.startTime.localeCompare(b.startTime)).map(apt => {
                const cust = customers.find(c => c.id === apt.customerId);
                const emp = employees.find(e => e.id === apt.employeeId);
                const svcs = (apt.serviceIds||[]).map(sid => services.find(s => s.id === sid)?.name).join(", ");
                return (
                  <tr key={apt.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{apt.startTime}</td>
                    <td style={{ padding: "10px 12px" }}>{cust?.name || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: emp?.color }} />
                        {emp?.name || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>{svcs}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{apt.totalPrice} €</td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge text={apt.status === "confirmed" ? "Bestätigt" : apt.status === "pending" ? "Ausstehend" : "Abgesagt"} color={apt.status === "confirmed" ? COLORS.success : apt.status === "pending" ? COLORS.warning : COLORS.danger} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* DASHBOARD_SORT_v1 -- kompakte, aufklappbare Info-Zeile (KI-Sichtbarkeit / KI-Chat / SMS).
          Einmal eingerichtet, braucht man die selten (Toby 15.07.) -- darum klein;
          Klick klappt den vollen bisherigen Block darunter auf. */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : `repeat(${canSeeFinancials ? 3 : 1}, 1fr)`, gap: 12, marginTop: 24, marginBottom: 12 }}>
        {[
          { key: "ai", label: "KI-Sichtbarkeit", hint: "ChatGPT & Co.", show: true },
          { key: "chat", label: "KI-Buchungs-Chat", hint: "Zoey diesen Monat", show: canSeeFinancials },
          { key: "sms", label: "SMS-Versand", hint: "diesen Monat", show: canSeeFinancials },
        ].filter(w => w.show).map(w => (
          <div key={w.key} onClick={() => setOpenInfoWidget(openInfoWidget === w.key ? null : w.key)}
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "var(--card)", border: `1px solid ${COLORS.border}`, borderRadius: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{w.label} <span style={{ fontWeight: 500, color: COLORS.textLight, fontSize: 12 }}>{w.hint}</span></span>
            <ChevronDown size={16} style={{ transform: openInfoWidget === w.key ? "rotate(180deg)" : "none", color: COLORS.textLight }} />
          </div>
        ))}
      </div>
      {openInfoWidget === "ai" && <AiVisibilityWidget />}
      {openInfoWidget === "chat" && canSeeFinancials && <AiChatStatsWidget />}
      {openInfoWidget === "sms" && canSeeFinancials && <SmsStatsWidget />}

      {/* DASHBOARD_SORT_v1 -- Charts ganz unten (Toby: Umsatz diese Woche ganz nach unten) */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : (canSeeFinancials ? "2fr 1fr" : "1fr"), gap: 16, marginBottom: 24 }}>
        {canSeeFinancials && (
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Umsatz diese Woche</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weekRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => `${v} €`} />
              <Bar dataKey="revenue" fill={COLORS.primary} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        )}
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Services heute</h3>
          {serviceBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={serviceBreakdown} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`} fontSize={11}>
                  {serviceBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textLight }}>Keine Termine heute</div>
          )}
        </Card>
      </div>
    </div>
  );
};